/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faExclamation } from '@fortawesome/free-solid-svg-icons/faExclamation';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { Router } from '@angular/router';
import { BehaviorSubject, iif, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, mergeMap, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { Store } from '@ngrx/store';
import { InterventionActionTypes } from '../../../store/intervention/intervention.action';
import { Actions, ofType } from '@ngrx/effects';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

/**
 * Component:
 * Intervention page displaying a list of interventions - child component of CatalogueComponent;
 */
@Component({
  selector: 'app-intervention',
  templateUrl: './intervention.component.html',
  styleUrls: ['./intervention.component.scss'],
})
export class InterventionComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faList = faList;
  faExclamation = faExclamation;

  // Filter
  public filter = {
    searchFilter: '',
    isCollaboratingOnly: true,
    typeOfIntervention: 0,
  };

  public isLoading$: Observable<boolean>;

  public param = { result_value: 0 };
  public isCollapse$: Observable<boolean>;

  public interventions$: Observable<InterventionInterface[]>;
  public collabInterventions$: Observable<InterventionInterface[]>;
  public nonCollabInterventions$: Observable<InterventionInterface[]>;
  public pagedInterventions$: Observable<InterventionInterface[]>;

  public hasPendingReviewingInterventions = false;

  // All isCollaboratingSubject intervention ids
  public collabInterventionIds: number[];

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private searchTextSubject = new BehaviorSubject<string>('');

  private interventionsSubject = new BehaviorSubject<InterventionInterface[]>([]);
  private pagedInterventionsSubject = new BehaviorSubject<InterventionInterface[]>([]);

  private interventions: InterventionInterface[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private helperService: HelperService,
    private store: Store<{
      collabInterventions: InterventionInterface[];
      nonCollabInterventions: InterventionInterface[];
    }>,
    private actions$: Actions,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.interventions$ = this.interventionsSubject.asObservable();
    this.pagedInterventions$ = this.pagedInterventionsSubject.asObservable();
    this.collabInterventions$ = this.store.select('collabInterventions');
    this.nonCollabInterventions$ = this.store.select('nonCollabInterventions');
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe(() => {
        this.applyInterventionFilter();
      }),
    );
    this.applyInterventionFilter();
  }

  setHasPendingReviewingInterventions(value: boolean) {
    this.hasPendingReviewingInterventions = value;
  }

  public applyInterventionFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadInterventionList(true).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  private getFormGuidanceType(typeOfIntervention: number) {
    if (typeOfIntervention === 2) {
      return 'accompanied';
    } else if (typeOfIntervention === 1) {
      return 'unaccompanied';
    } else {
      return undefined;
    }
  }

  public reloadInterventionList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    const paramGuidanceType = this.getFormGuidanceType(this.filter.typeOfIntervention);
    this.store.dispatch({
      type: InterventionActionTypes.getInterventionsOfCollabStudyType,
      payload: { type: 'intervention', interventionType: paramGuidanceType, include: 'roles', typeOfParentStudy: 'study' },
    });
    return this.actions$.pipe(
      ofType(
        InterventionActionTypes.getInterventionsOfCollabStudySuccessType,
        InterventionActionTypes.getInterventionsOfCollabStudyErrorType,
      ),
      take(1),
      switchMap((result: any) =>
        iif(
          () => result.type === InterventionActionTypes.getInterventionsOfCollabStudySuccessType,
          this.collabInterventions$,
          of([]),
        ),
      ),
      switchMap((result) =>
        iif(
          () => this.filter.isCollaboratingOnly,
          this.setInterventionCollabOnly(result, setFirst, pageIndex, pageSize),
          this.setInterventionNonCollabOnly(result, paramGuidanceType, setFirst, pageIndex, pageSize),
        ),
      ),
    );
  }

  public setInterventionCollabOnly(
    interventions: InterventionInterface[],
    setFirst: boolean | undefined,
    pageIndex: number,
    pageSize: number,
  ) {
    this.interventions = interventions;
    this.collabInterventionIds = interventions.map((obj) => obj.id);
    return this.search(this.filter.searchFilter).pipe(
      switchMap(
        () => this.helper.setPagedContent(this.interventionsSubject, this.pagedInterventionsSubject, setFirst, pageIndex, pageSize),
      ),
    );
  }

  public setInterventionNonCollabOnly(
    interventions: InterventionInterface[],
    paramGuidanceType: 'accompanied' | 'unaccompanied' | undefined,
    setFirst: boolean | undefined,
    pageIndex: number,
    pageSize: number,
  ) {
    this.interventions = interventions;
    this.collabInterventionIds = interventions.map((obj) => obj.id);
    this.store.dispatch({
      type: InterventionActionTypes.getInterventionsOfNonCollabStudyType,
      payload: { type: 'intervention', interventionType: paramGuidanceType, include: 'roles', typeOfParentStudy: 'study' },
    });
    return this.nonCollabInterventions$.pipe(
      switchMap((nonCollab) => {
        this.interventions = this.helper
          .uniqueById(this.interventions.concat(nonCollab))
          .sort((a, b) => a.id - b.id);
        return this.search(this.filter.searchFilter).pipe(
          switchMap(() =>
            this.helper.setPagedContent(this.interventionsSubject, this.pagedInterventionsSubject, setFirst, pageIndex, pageSize),
          ),
        );
      }),
    );
  }

  public isCollabId(id: number): boolean {
    return this.collabInterventionIds !== undefined ? this.collabInterventionIds.includes(id) : false;
  }

  // Navigates to detailed interventions
  public getDetailedIntervention(id: number): void {
    this.router.navigateByUrl(`/interventions/${id}`);
  }

  // Get intervention instance details
  public getDetailedInterventionInstance(id: number): void {
    this.router.navigateByUrl(`interventions/${id}/instances`);
  }

  // Get study name
  public getDetailedStudy(id: number): void {
    this.router.navigateByUrl(`groups/${id}`);
  }

  public updatePagedInterventions(event: InterventionInterface[]) {
    if (event) {
      this.pagedInterventionsSubject.next(event);
    }
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    const paramGuidanceType = this.getFormGuidanceType(this.filter.typeOfIntervention);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        const filter = this.filter.searchFilter.trim().toLowerCase();
        this.interventionsSubject.next(
          this.helper.filterInterventionsBy(this.interventions, filter, paramGuidanceType),
        );
        return of(true);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
